'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';

import PrimaryButton from '@workspace/lib/components/molecules/buttons/PrimaryButton';
import TextButton from '@workspace/lib/components/molecules/buttons/TextButton';
import {
  DialogContent,
  DialogHeader,
  Dialog as DialogRoot,
  DialogTitle,
} from '@workspace/lib/components/molecules/dialog-parts';

import { useSessionTimeout } from './use-session-timeout';

/**
 * Global session timeout warning dialog.
 *
 * Renders a non-dismissible modal 2 minutes before the SSO session JWT expires,
 * showing a live countdown and offering the user a "Stay logged in" action that
 * silently refreshes the token. The "Log out" button performs a real sign-out
 * via the SSO portal's logout endpoint.
 *
 * The dialog cannot be closed via Escape key or clicking outside — the user
 * must explicitly choose an action. This prevents accidental logout from
 * incidental dismiss events.
 *
 * Mount this component inside the provider tree, after AuthProvider, so it can
 * access useAuth() via the useSessionTimeout hook.
 */
export function SessionTimeoutDialog() {
  const { showWarning, secondsRemaining, extendSession, logout } =
    useSessionTimeout();
  const t = useTranslations();

  // Memoize static translation lookups so they are not recomputed on every
  // 1-second countdown tick. Only the `body` string (with {time}) changes.
  const { title, stayText, logoutText, hint } = useMemo(
    () => ({
      title: t.has('sessionTimeout.title')
        ? t('sessionTimeout.title')
        : 'Your session is about to expire',
      stayText: t.has('sessionTimeout.stayLoggedIn')
        ? t('sessionTimeout.stayLoggedIn')
        : 'Stay logged in',
      logoutText: t.has('sessionTimeout.logout')
        ? t('sessionTimeout.logout')
        : 'Log out',
      hint: t.has('sessionTimeout.hint')
        ? t('sessionTimeout.hint')
        : 'Click "Stay logged in" to continue your application.',
    }),
    [t],
  );

  if (!showWarning) return null;

  const minutes = Math.floor(secondsRemaining / 60);
  const seconds = secondsRemaining % 60;
  const timeDisplay = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  const body = t.has('sessionTimeout.body')
    ? t('sessionTimeout.body', { time: timeDisplay })
    : `For your security, your session will expire in ${timeDisplay}.`;

  return (
    <DialogRoot open={showWarning}>
      <DialogContent
        className='w-[calc(100%-2rem)] max-w-md rounded-2xl bg-white px-6 py-8'
        // Prevent Escape key and outside-click from dismissing the dialog.
        // The user must explicitly choose "Stay logged in" or "Log out".
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        closeButtonClassName='hidden'
      >
        <DialogHeader className='mb-4'>
          <DialogTitle className='pr-8 text-center text-xl font-bold'>
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className='space-y-4'>
          <p className='font-nunito text-black-secondary text-center'>{body}</p>
          <p className='font-nunito text-center text-sm text-neutral-800'>
            {hint}
          </p>

          <div className='flex flex-col items-center justify-center gap-3 pt-2'>
            <PrimaryButton
              type='button'
              textContent={stayText}
              onClick={extendSession}
              disabled={false}
            />
            <TextButton
              type='button'
              textContent={logoutText}
              onClick={logout}
              className='w-full justify-center'
            />
          </div>
        </div>
      </DialogContent>
    </DialogRoot>
  );
}
